import { RSI, MACD, EMA, SMA, BollingerBands, ATR, Stochastic, ADX, OBV } from 'technicalindicators';

// ---- Signal strategies (mirror of server/src/lib/ta.ts — keep both in sync) ----
const MIN_REL_VOL = 1.0;
const ADX_TREND_MIN = 25;
const ADX_RANGE_MAX = 20;

const scoreToSignal = (score, strong) => {
  if (score >= strong) return 'STRONG BUY';
  if (score > 0) return 'BUY';
  if (score <= -strong) return 'STRONG SELL';
  if (score < 0) return 'SELL';
  return 'HOLD';
};

const confluenceScore = (i) => {
  let s = 0;
  if (i.rsi <= 35) s += 2;
  if (i.rsi >= 65) s -= 2;
  if (i.macdHist > 0) s += 1; else if (i.macdHist < 0) s -= 1;
  if (i.price > i.ema20) s += 1; else s -= 1;
  if (i.price <= i.bbLower * 1.02) s += 1;
  if (i.price >= i.bbUpper * 0.98) s -= 1;
  if (i.price > i.vwap) s += 1; else s -= 1;
  if (i.stochK <= 20) s += 1;
  if (i.stochK >= 80) s -= 1;
  if (i.obvTrend === 'Accumulation') s += 1;
  if (i.obvTrend === 'Distribution') s -= 1;
  return s;
};

const STRATEGIES = {
  confluence: (i) => scoreToSignal(confluenceScore(i), 3),
  // Revamped trend-following: fresh, confirmed, non-extended ignition only (mirror of ta.ts).
  momentum: (i) => {
    if (i.ema9 < i.ema21 && i.price < i.vwap && i.macdHist < 0) return 'STRONG SELL';
    const trendUp = i.ema9 > i.ema21 && i.price > i.vwap;
    const rising = i.macdHist > 0 && i.macdHist > i.prevMacdHist;
    const rsiOk = i.rsi >= 50 && i.rsi <= 68;
    const vol = i.relVol > 1.3;
    const notExtended = i.rsi <= 68 && (i.vwap <= 0 || i.price <= i.vwap * 1.15) && (i.ema9 <= 0 || i.price <= i.ema9 * 1.20);
    if (rising && trendUp && rsiOk && vol && notExtended) return 'STRONG BUY';
    if (trendUp && i.macdHist > 0 && i.rsi >= 50 && i.rsi <= 70) return 'BUY';
    if (i.price < i.ema21 && i.macdHist < 0) return 'SELL';
    return 'HOLD';
  },
  breakout: (i) => {
    if (i.price >= i.recentHigh && i.relVol >= 1.5 && i.price > i.vwap) return 'STRONG BUY';
    if (i.price < i.ema9) return 'STRONG SELL';
    if (i.price >= i.recentHigh && i.price > i.vwap) return 'BUY';
    return 'HOLD';
  },
  meanReversion: (i) => {
    if (i.adx >= ADX_RANGE_MAX) return 'HOLD';
    if (i.rsi <= 30 && i.stochK <= 20 && i.price <= i.bbLower * 1.02) return 'STRONG BUY';
    if (i.rsi >= 70 && i.stochK >= 80 && i.price >= i.bbUpper * 0.98) return 'STRONG SELL';
    return 'HOLD';
  },
  strict: (i) => {
    const s = confluenceScore(i);
    if (s >= 4 && i.relVol >= 1.2 && i.obvTrend === 'Accumulation') return 'STRONG BUY';
    if (s <= -4) return 'STRONG SELL';
    if (s > 0) return 'BUY';
    if (s < 0) return 'SELL';
    return 'HOLD';
  },
  scalp: (i) => {
    const trendUp = i.ema9 > i.ema21 && i.price > i.vwap;
    const breakout = i.price >= i.recentHigh;
    const vol = i.relVol >= 1.3;
    const momentum = i.macdHist > 0 && i.macdHist > i.prevMacdHist && i.rsi >= 50 && i.rsi <= 72;
    if (trendUp && breakout && vol && momentum) return 'STRONG BUY';
    if (i.ema9 < i.ema21 && i.price < i.vwap) return 'STRONG SELL';
    if (trendUp && breakout) return 'BUY';
    return 'HOLD';
  }
};

const isBuySignal = (s) => s === 'BUY' || s === 'STRONG BUY';
const applyQualityGates = (signal, i, strategy) => {
  if (!isBuySignal(signal)) return signal;
  if (i.relVol < MIN_REL_VOL) return 'HOLD';
  if (strategy === 'momentum' || strategy === 'breakout' || strategy === 'scalp' || strategy === 'strict') {
    if (i.adx < ADX_TREND_MIN || i.pdi <= i.mdi) return 'HOLD';
  }
  return signal;
};

const generateFallbackTA = (currentPrice, entryPrice) => {
  const diff = currentPrice * 0.1;
  const SH = currentPrice + diff;
  const SL = currentPrice - diff;
  const P = currentPrice;

  return {
    indicators: {
      rsi: { value: '50.0', status: 'Neutral (Insufficient Data)' },
      macd: { value: '0.000', status: 'Converging' },
      ema: { value: currentPrice.toFixed(6), status: 'Consolidating' },
      sma: { value: currentPrice.toFixed(6), status: 'Consolidating' },
      bb: { value: 'Band Width', status: 'Mid-Band' },
      atr: { value: (currentPrice * 0.05).toFixed(6), status: 'Volatility' },
      stoch: { value: '50.0 / 50.0', status: 'Neutral' },
      adx: { value: '20.0', status: 'Ranging' },
      obv: { value: '0', status: 'Neutral' },
      relVol: { value: '1.00x', status: 'Average' },
      vwap: { value: currentPrice.toFixed(6), status: 'Consolidating' }
    },
    fibonacci: {
      lvl236: SH - 0.236 * diff,
      lvl382: SH - 0.382 * diff,
      lvl500: SH - 0.500 * diff,
      lvl618: SH - 0.618 * diff,
      lvl786: SH - 0.786 * diff,
    },
    supportResistance: {
      r3: P + 2 * diff,
      r2: P + diff,
      r1: 2 * P - SL,
      pivot: P,
      s1: 2 * P - SH,
      s2: P - diff,
      s3: P - 2 * diff
    },
    suggestions: {
      hardStop: { price: currentPrice * 0.9, pct: 10.0 },
      takeProfit: { price: currentPrice * 1.3, pct: 30.0 },
      trailingStop: { pct: 15.0 }
    },
    signal: 'HOLD',
    signalColor: 'text-slate-400',
    bgColor: 'bg-slate-500/10 border-slate-500/20',
    reasoning: 'Insufficient historical data to calculate accurate market momentum.'
  };
};

export const calculateRealTA = (ohlcv, currentPrice, entryPrice, strategy = 'confluence', signalQuality = true) => {
  if (!ohlcv || ohlcv.length < 30) {
    return generateFallbackTA(currentPrice, entryPrice);
  }

  const highs = ohlcv.map(c => c.high);
  const lows = ohlcv.map(c => c.low);
  const closes = ohlcv.map(c => c.close);
  const volumes = ohlcv.map(c => c.volume);

  // Apply real mathematical formulas
  const rsiArray = RSI.calculate({ values: closes, period: 14 });
  const macdArray = MACD.calculate({ values: closes, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9, SimpleMAOscillator: false, SimpleMASignal: false });
  const emaArray = EMA.calculate({ values: closes, period: 20 });
  const ema9Array = EMA.calculate({ values: closes, period: 9 });
  const ema21Array = EMA.calculate({ values: closes, period: 21 });
  const smaArray = SMA.calculate({ values: closes, period: 20 });
  const bbArray = BollingerBands.calculate({ values: closes, period: 20, stdDev: 2 });
  const atrArray = ATR.calculate({ high: highs, low: lows, close: closes, period: 14 });
  const stochArray = Stochastic.calculate({ high: highs, low: lows, close: closes, period: 14, signalPeriod: 3 });
  const adxArray = ADX.calculate({ high: highs, low: lows, close: closes, period: 14 });
  const obvArray = OBV.calculate({ close: closes, volume: volumes });

  // Custom VWAP
  let cumulativeVol = 0;
  let cumulativeTPV = 0;
  const vwapArray = closes.map((close, i) => {
    const tp = (highs[i] + lows[i] + close) / 3;
    cumulativeVol += volumes[i];
    cumulativeTPV += tp * volumes[i];
    return cumulativeVol ? cumulativeTPV / cumulativeVol : close;
  });

  // Fibonacci & Support/Resistance based on recent price action
  const SH = Math.max(...highs);
  const SL = Math.min(...lows);
  const diff = SH - SL;
  const P = (SH + SL + currentPrice) / 3;

  const r1 = 2 * P - SL;
  const s1 = 2 * P - SH;
  const r2 = P + diff;
  const s2 = P - diff;
  const r3 = r1 + diff;
  const s3 = s1 - diff;

  const fibonacci = {
    lvl236: SH - 0.236 * diff,
    lvl382: SH - 0.382 * diff,
    lvl500: SH - 0.500 * diff,
    lvl618: SH - 0.618 * diff,
    lvl786: SH - 0.786 * diff,
  };

  const supportResistance = {
    r3, r2, r1, pivot: P, s1, s2, s3
  };

  // Extract latest values
  const currentRSI = rsiArray.length > 0 ? rsiArray[rsiArray.length - 1] : 50;
  const prevMACD = macdArray.length > 1 ? macdArray[macdArray.length - 2] : null;
  const currentMACD = macdArray.length > 0 ? macdArray[macdArray.length - 1] : { MACD: 0, signal: 0, histogram: 0 };
  const currentEMA = emaArray.length > 0 ? emaArray[emaArray.length - 1] : currentPrice;
  const currentSMA = smaArray.length > 0 ? smaArray[smaArray.length - 1] : currentPrice;
  const currentBB = bbArray.length > 0 ? bbArray[bbArray.length - 1] : { upper: currentPrice*1.05, middle: currentPrice, lower: currentPrice*0.95 };
  const currentATR = atrArray.length > 0 ? atrArray[atrArray.length - 1] : (currentPrice * 0.05);
  const currentStoch = stochArray.length > 0 ? stochArray[stochArray.length - 1] : { k: 50, d: 50 };
  const currentADX = adxArray.length > 0 ? adxArray[adxArray.length - 1] : { adx: 20, pdi: 20, mdi: 20 };
  const currentOBV = obvArray.length > 0 ? obvArray[obvArray.length - 1] : 0;
  const currentVWAP = vwapArray[vwapArray.length - 1];

  // Determine Statuses
  let rsiStatus = 'Neutral';
  if (currentRSI >= 70) rsiStatus = 'Overbought';
  else if (currentRSI <= 30) rsiStatus = 'Oversold';

  let macdTrend = 'Converging';
  if (prevMACD) {
    if (currentMACD.histogram > 0 && prevMACD.histogram <= 0) macdTrend = 'Bullish Crossover';
    else if (currentMACD.histogram < 0 && prevMACD.histogram >= 0) macdTrend = 'Bearish Crossover';
    else if (currentMACD.histogram > 0 && currentMACD.histogram > prevMACD.histogram) macdTrend = 'Bullish Expanding';
    else if (currentMACD.histogram < 0 && currentMACD.histogram < prevMACD.histogram) macdTrend = 'Bearish Expanding';
  }

  const emaTrend = currentPrice > currentEMA ? 'Above (Bullish)' : 'Below (Bearish)';
  const smaTrend = currentPrice > currentSMA ? 'Above (Bullish)' : 'Below (Bearish)';

  let bbStatus = 'Mid-Band (Consolidating)';
  if (currentPrice >= currentBB.upper * 0.99) bbStatus = 'Testing Upper Band';
  else if (currentPrice <= currentBB.lower * 1.01) bbStatus = 'Testing Lower Band';

  let stochStatus = 'Neutral';
  if (currentStoch.k >= 80) stochStatus = 'Overbought';
  else if (currentStoch.k <= 20) stochStatus = 'Oversold';

  let adxStatus = currentADX.adx > 25 ? 'Trending' : 'Ranging';
  if (currentADX.pdi > currentADX.mdi) adxStatus += ' (Bullish)';
  else if (currentADX.mdi > currentADX.pdi) adxStatus += ' (Bearish)';

  let obvTrend = 'Neutral';
  const prevOBV = obvArray.length > 1 ? obvArray[obvArray.length - 2] : 0;
  if (currentOBV > prevOBV) obvTrend = 'Accumulation';
  else if (currentOBV < prevOBV) obvTrend = 'Distribution';

  const vwapStatus = currentPrice > currentVWAP ? 'Above (Bullish)' : 'Below (Bearish)';

  const avgVol = volumes.slice(-20).reduce((a, b) => a + b, 0) / 20;
  const lastVol = volumes[volumes.length - 1];
  let volTrend = 'Average';
  if (lastVol > avgVol * 1.5) volTrend = 'Surging (High Momentum)';
  else if (lastVol < avgVol * 0.5) volTrend = 'Declining (Low Momentum)';
  const relVol = avgVol > 0 ? (lastVol / avgVol) : 1;

  // Signal via the selected strategy (mirrors the engine's registry), then the
  // cross-cutting quality gates. Indicators above feed the shared snapshot below.
  const currentEMA9 = ema9Array.length > 0 ? ema9Array[ema9Array.length - 1] : currentPrice;
  const currentEMA21 = ema21Array.length > 0 ? ema21Array[ema21Array.length - 1] : currentPrice;
  const prevMacdHist = prevMACD ? (prevMACD.histogram ?? 0) : 0;
  const recentHighs = highs.slice(-21, -1);
  const recentLows = lows.slice(-21, -1);
  const recentHigh = recentHighs.length ? Math.max(...recentHighs) : currentPrice;
  const recentLow = recentLows.length ? Math.min(...recentLows) : currentPrice;

  const ind = {
    price: currentPrice,
    rsi: currentRSI,
    macdHist: currentMACD.histogram ?? 0,
    prevMacdHist,
    ema9: currentEMA9,
    ema21: currentEMA21,
    ema20: currentEMA,
    sma20: currentSMA,
    bbUpper: currentBB.upper,
    bbLower: currentBB.lower,
    atr: currentATR,
    stochK: currentStoch.k,
    adx: currentADX.adx,
    pdi: currentADX.pdi,
    mdi: currentADX.mdi,
    obvTrend,
    vwap: currentVWAP,
    relVol,
    recentHigh,
    recentLow
  };

  let signal = (STRATEGIES[strategy] || STRATEGIES.confluence)(ind);
  if (signalQuality) signal = applyQualityGates(signal, ind, strategy);

  let signalColor = 'text-slate-400';
  let bgColor = 'bg-slate-500/10 border-slate-500/20';
  if (signal.includes('BUY')) {
    signalColor = 'text-green-400';
    bgColor = 'bg-green-500/10 border-green-500/20';
  } else if (signal.includes('SELL')) {
    signalColor = 'text-red-400';
    bgColor = 'bg-red-500/10 border-red-500/20';
  }

  let reasoning = `Market is showing ${volTrend.toLowerCase()} volume. `;
  if (entryPrice) {
    reasoning = `Price is currently ${currentPrice > entryPrice ? 'above' : 'below'} entry. ` + reasoning;
  }
  if (signal.includes('BUY')) {
    reasoning += `Momentum is building. RSI is ${rsiStatus} (${currentRSI.toFixed(1)}) and MACD shows a ${macdTrend}.`;
  } else if (signal.includes('SELL')) {
    reasoning += `Momentum is weakening. MACD shows a ${macdTrend} and RSI is ${rsiStatus} (${currentRSI.toFixed(1)}).`;
  } else {
    reasoning += `RSI is neutral (${currentRSI.toFixed(1)}). Wait for a breakout confirmation.`;
  }

  // ATR-based risk percentages, clamped to sane bounds so extreme volatility
  // can't produce absurd stops/targets. Prices are derived from the clamped
  // percentages so the displayed price and % always agree.
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  // Strategy-aware ATR risk profiles (mirror of server/src/lib/ta.ts RISK_PROFILE).
  // scalp = tight all-round; strict = tighter initial hard stop + wide TP (pairs with the
  // breakeven-arm exit); others = original default.
  const RISK_PROFILE = {
    default:  { slMul: 2, slMin: 5, slMax: 40, tpMul: 3, tpMin: 10, tpMax: 500, tsMul: 1.5, tsMin: 3, tsMax: 40 },
    scalp:    { slMul: 1.5, slMin: 6, slMax: 12, tpMul: 2.5, tpMin: 20, tpMax: 60, tsMul: 1.2, tsMin: 6, tsMax: 12 },
    strict:   { slMul: 1.2, slMin: 8, slMax: 14, tpMul: 3, tpMin: 10, tpMax: 500, tsMul: 1.5, tsMin: 3, tsMax: 40 },
    momentum: { slMul: 1.5, slMin: 8, slMax: 16, tpMul: 3, tpMin: 10, tpMax: 500, tsMul: 1.5, tsMin: 3, tsMax: 30 }
  };
  const prof = RISK_PROFILE[strategy] || RISK_PROFILE.default;

  const rawSlPct = currentPrice > 0 ? ((currentATR * prof.slMul) / currentPrice) * 100 : 10;
  const slPct = clamp(rawSlPct, prof.slMin, prof.slMax);
  const slPrice = currentPrice * (1 - slPct / 100);

  const rawTpPct = currentPrice > 0 ? ((currentATR * prof.tpMul) / currentPrice) * 100 : 30;
  const tpPct = clamp(rawTpPct, prof.tpMin, prof.tpMax);
  const tpPrice = currentPrice * (1 + tpPct / 100);

  const rawTsPct = currentPrice > 0 ? ((currentATR * prof.tsMul) / currentPrice) * 100 : 15;
  const tsPct = clamp(rawTsPct, prof.tsMin, prof.tsMax);

  return {
    indicators: {
      rsi: { value: currentRSI.toFixed(1), status: rsiStatus },
      macd: { value: currentMACD.MACD.toFixed(6), status: macdTrend },
      ema: { value: currentEMA.toFixed(6), status: emaTrend },
      sma: { value: currentSMA.toFixed(6), status: smaTrend },
      bb: { value: 'Band Width', status: bbStatus },
      atr: { value: currentATR.toFixed(6), status: 'Volatility' },
      stoch: { value: `${currentStoch.k.toFixed(1)} / ${currentStoch.d.toFixed(1)}`, status: stochStatus },
      adx: { value: currentADX.adx.toFixed(1), status: adxStatus },
      obv: { value: currentOBV > 1000000 ? (currentOBV/1000000).toFixed(2)+'M' : (currentOBV > 1000 ? (currentOBV/1000).toFixed(1)+'k' : currentOBV.toFixed(0)), status: obvTrend },
      relVol: { value: relVol.toFixed(2) + 'x', status: volTrend },
      vwap: { value: currentVWAP.toFixed(6), status: vwapStatus }
    },
    fibonacci,
    supportResistance,
    suggestions: {
      hardStop: { price: slPrice, pct: slPct },
      takeProfit: { price: tpPrice, pct: tpPct },
      trailingStop: { pct: tsPct }
    },
    signal,
    signalColor,
    bgColor,
    reasoning
  };
};

export const formatChartData = (ohlcv) => {
  if (!ohlcv || ohlcv.length === 0) return [];

  const closes = ohlcv.map(c => c.close);
  const rsiArray = RSI.calculate({ values: closes, period: 14 });
  const macdArray = MACD.calculate({ values: closes, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9, SimpleMAOscillator: false, SimpleMASignal: false });

  return ohlcv.map((candle, index) => {
    const date = new Date(candle.time * 1000);
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' });

    const rsiIndex = index - (ohlcv.length - rsiArray.length);
    const macdIndex = index - (ohlcv.length - macdArray.length);

    return {
      time: timeStr,
      price: candle.close,
      rsi: rsiIndex >= 0 ? rsiArray[rsiIndex] : null,
      macd: macdIndex >= 0 ? macdArray[macdIndex].MACD : null,
      signal: macdIndex >= 0 ? macdArray[macdIndex].signal : null,
      hist: macdIndex >= 0 ? macdArray[macdIndex].histogram : null,
      volume: candle.volume
    };
  });
};